import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ProjectTemplateRequest,
  ProjectTemplateResponse,
  CompactProjectTemplateResponse,
  ProjectTemplateId,
  TemplateStepResponse,
} from '@business/entities/project';
import { ProjectTemplateAPI } from '@business/api/projectTemplate';
import { R } from '@core/utils/r';
import { ProjectTemplateFolderId } from '@business/entities/project/ProjectTemplateFolderResponse';

export class ProjectTemplateBloc extends BaseBloc {
  private readonly projectTempRepo = new Repository<CompactProjectTemplateResponse | ProjectTemplateResponse>({
    getItemId: template => template.id,
  });

  readonly projectTemplates$ = this.projectTempRepo.items$.pipe(map(items => R.sortBy(items, i => i.name.toLowerCase())));
  readonly selectProjectTemplate = this.projectTempRepo.selectItem;

  readonly selectStep = (projectId: ProjectTemplateId, stepId: number) =>
    this.projectTempRepo.selectItem(projectId).pipe(
      map(project => {
        if (!project) return undefined;
        const allSteps = R.flatMap((project as ProjectTemplateResponse).sections || [], section => section.steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );

  onReset() {
    this.projectTempRepo.reset();
  }

  fetchProjectTemplates = (folderId?: ProjectTemplateFolderId) => {
    return ProjectTemplateAPI.fetchProjectTemplates(folderId).pipe(
      this.projectTempRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_pro_templates_error') }),
    );
  };

  fetchProjectTemplate = (id: ProjectTemplateId) => {
    return ProjectTemplateAPI.fetchProjectTemplate(id).pipe(
      this.projectTempRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_pro_templates_error') }),
    );
  };

  createProjectTemplate = (project: ProjectTemplateRequest) => {
    return ProjectTemplateAPI.createProjectTemplate(project).pipe(
      this.projectTempRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_project_template'),
      }),
    );
  };

  updateProjectTemplate = (id: ProjectTemplateId, project: ProjectTemplateRequest) => {
    return ProjectTemplateAPI.updateProjectTemplate(id, project).pipe(
      this.projectTempRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project_template'),
      }),
    );
  };

  duplicateProjectTemplate = (id: ProjectTemplateId, template: ProjectTemplateResponse) => {
    return showDeleteConfirmation('Duplicate Template', 'Do you really want to duplicate this Template?', { btnLabel: 'Duplicate' }).pipe(
      switchMap(() => ProjectTemplateAPI.duplicateProjectTemplate(id, template)),
      this.projectTempRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_project_template'),
      }),
    );
  };

  deleteProjectTemplate = (id: ProjectTemplateId) => {
    return showDeleteConfirmation('Delete Template', 'Do you really want to remove this template?').pipe(
      switchMap(() => ProjectTemplateAPI.deleteProjectTemplate(id)),
      this.projectTempRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_project_template'),
      }),
    );
  };

  /* Sections */
  createTemplateSection = (templateId: ProjectTemplateId, name: string, isPublic = false) => {
    return ProjectTemplateAPI.createTemplateSection(templateId, name, isPublic).pipe(
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateTemplateSection = (templateId: ProjectTemplateId, id: number, name: string, isPublic = false) => {
    return ProjectTemplateAPI.updateTemplateSection(id, name, isPublic).pipe(
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteTemplateSection = (templateId: ProjectTemplateId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => ProjectTemplateAPI.deleteTemplateSection(id)),
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  sortSections = (templateId: ProjectTemplateId, sectionIds: number[]) => {
    return ProjectTemplateAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const template = this.projectTempRepo.getItem(templateId)! as ProjectTemplateResponse;
        const newSections = template.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.projectTempRepo.updateOne({
          id: templateId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };

  /* Steps */

  fetchStep = (templateId: ProjectTemplateId, id: number) => {
    return ProjectTemplateAPI.fetchStep(id).pipe(
      handleMessage({
        error: requestMessage('fetch_step'),
      }),
    );
  };

  createTemplateSectionStep = (templateId: ProjectTemplateId, sectionId: number, data: Partial<TemplateStepResponse>, parentStepId?: number) => {
    return ProjectTemplateAPI.createTemplateStep(sectionId, data, parentStepId).pipe(
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('create_template_section_step'),
      }),
    );
  };

  updateTemplateSectionStep = (templateId: ProjectTemplateId, id: number, data: Partial<TemplateStepResponse>) => {
    return ProjectTemplateAPI.updateTemplateStep(id, data).pipe(
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('update_template_section_step'),
      }),
    );
  };

  deleteTemplateSectionStep = (templateId: ProjectTemplateId, id: number) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => ProjectTemplateAPI.deleteTemplateStep(id)),
      switchMap(() => this.fetchProjectTemplate(templateId)),
      handleMessage({
        type: requestMessage('delete_template_section_step'),
      }),
    );
  };

  sortSectionSteps = (templateId: ProjectTemplateId, sectionId: number, stepIds: number[]) => {
    return ProjectTemplateAPI.sortSectionSteps(stepIds).pipe(
      tap(() => {
        const template = this.projectTempRepo.getItem(templateId)! as ProjectTemplateResponse;
        const affectedSection = R.find(template.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = template.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.projectTempRepo.updateOne({
          id: templateId,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  createOrUpdateTemplate = (templateId: ProjectTemplateId, template: Partial<ProjectTemplateResponse>) => {
    return ProjectTemplateAPI.createOrUpdateTemplate(templateId, template).pipe(
      this.projectTempRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_project_template'),
      }),
    );
  };
}
